import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { AnimatePresence, motion } from "framer-motion";

import InfiniteLoader from "@/components/InfiniteLoader";
import { SectionHeading } from "@/components/Layout/Parts";
import ScrollableTable from "@/components/ScrollableTable";
import {
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from "@/components/Table";
import {
  GoogleAmazonProductResource,
  PaginatedResponse,
  SortState,
} from "@/types/resources";
import { api } from "@/utils/api";
import { SortDirection } from "@/utils/enums";
import Button from "@/components/Button";
import ProductsFilter from "./ProductsFilter";
import ProductsTableRow from "./ProductsTableRow";
import ProductsPostModal from "./ProductsPostModal";

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DESC,
  property: "times_repeated",
};

const StyledTable = styled(Table)`
  border-collapse: separate;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    min-width: 120px;
    &:nth-child(1) {
      width: 60px;
      min-width: 60px;
      padding-left: 24px;
      padding-right: 12px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:nth-child(2) {
      width: 146px;
      min-width: 146px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 60px;
      top: 0;
      bottom: 0;
    }
    &:nth-child(3) {
      width: 220px;
      min-width: 220px;
      max-width: 220px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 206px;
      padding-right: 24px;
      top: 0;
      bottom: 0;
    }
    &:nth-child(5) {
      min-width: 240px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
  td {
    font-size: 12px;
  }
`;

const StyledTableHeader = styled(TableHeader)`
  position: relative;
`;

const ButtonsHolder = styled.div`
  display: flex;
  margin-left: auto;
`;

const StyledSectionHeading = styled(SectionHeading)`
  margin-bottom: 0;
`;

export const CheckboxHolder = styled.div`
  position: relative;
`;

export const CheckboxLabel = styled.label`
  display: block;
  height: 24px;
  width: 24px;
  right: 10px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  background-color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purpleLighter};
  }
`;

const UploadButtonHolder = styled(motion.div)`
  position: sticky;
  top: 128px;
  display: flex;
  z-index: 2;
  justify-content: flex-end;
  transform-origin: right -97px;
`;

const UploadButton = styled(Button)`
  padding: 0 24px;
  position: absolute;
  top: -124px;
  box-shadow: 0px 10px 30px rgba(34, 34, 34, 0.12),
    0px 20px 100px rgba(34, 34, 34, 0.06);
  min-width: 284px;
`;

export const Checkbox = styled.input<{ $shouldUncheck?: boolean }>`
  display: none;
  &:checked + ${CheckboxLabel} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: transparent;
    &:after {
      content: "";
      position: absolute;
      left: 8px;
      top: 3px;
      width: 8px;
      height: 14px;
      border: solid ${({ theme }) => theme.colors.white};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      ${({ $shouldUncheck }) =>
        $shouldUncheck &&
        css`
          background-color: ${({ theme }) => theme.colors.white};
          border-radius: 20px;
          width: 12px;
          height: 2px;
          border: 0;
          border-width: 0;
          transform: rotate(0deg);
          top: 0;
          left: 0;
          right: 1px;
          bottom: 0;
          margin: auto;
        `}
    }
  }
`;

const ProductsTable = () => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [showPostModal, setShowPostModal] = useState(false);

  const [filter, setFilter] = useState<{
    rating?: number;
    minPrice?: number;
    maxPrice?: number;
    minDiscount?: number;
    minReviews?: number;
    maxReviews?: number;
  }>({ rating: 4.3 });

  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);

  const { id } = useParams();

  const fetchProducts = ({ pageParam = "/v1/google-amazon-products" }) => {
    return api.get<never, PaginatedResponse<GoogleAmazonProductResource>>(
      pageParam,
      {
        params: {
          "filter[rating]": filter.rating,
          "filter[min_price]": filter.minPrice,
          "filter[max_price]": filter.maxPrice,
          "filter[min_discount]": filter.minDiscount,
          "filter[min_reviews]": filter.minReviews,
          "filter[max_reviews]": filter.maxReviews,
          "filter[search_id]": id,
          ...(sort.direction !== "DEFAULT" && {
            sort: `${sort.direction === "DESC" ? "-" : ""}${sort.property}`,
          }),
        },
      }
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<GoogleAmazonProductResource>>(
    ["google_amazon_products", { id, sort, filter }],
    fetchProducts,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    }
  );

  const products = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<GoogleAmazonProductResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );

  return (
    <>
      <AnimatePresence>
        {!!selectedProducts.length && (
          <UploadButtonHolder
            initial={{ scale: 0, opacity: 0.3 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0.3 }}
          >
            <UploadButton
              onClick={() => setShowPostModal(true)}
              color="inverted"
            >
              Upload to BP shopping list ({selectedProducts.length})
            </UploadButton>
          </UploadButtonHolder>
        )}
      </AnimatePresence>
      <StyledTableHeader>
        <StyledSectionHeading>Prodcuts list</StyledSectionHeading>
        <ButtonsHolder>
          <ProductsFilter filter={filter} setFilter={setFilter} />
        </ButtonsHolder>
      </StyledTableHeader>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>
                <CheckboxHolder>
                  <Checkbox
                    id="all-products"
                    name="products"
                    type="checkbox"
                    checked={!!selectedProducts.length}
                    $shouldUncheck={!!selectedProducts.length}
                    onChange={() => {
                      !!selectedProducts.length
                        ? setSelectedProducts([])
                        : setSelectedProducts(
                            products.map((product) => product.id)
                          );
                    }}
                  />
                  <CheckboxLabel htmlFor="all-products" />
                </CheckboxHolder>
              </Th>
              <Th>Image</Th>
              <Th>Product Name</Th>
              <SortableTh
                property="times_repeated"
                title="Times Appeared"
                setSort={setSort}
                sort={sort}
              />
              <Th>Ai Description</Th>
              <SortableTh
                property="rating"
                title="Rating"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="reviews_count"
                title="Reviews count"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="price_strikethrough"
                title="Price"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="price"
                title="Discounted price"
                setSort={setSort}
                sort={sort}
              />
              <SortableTh
                property="discount"
                title="Discount"
                setSort={setSort}
                sort={sort}
              />
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!products.length}
            >
              {products?.map((product) => (
                <ProductsTableRow
                  key={product.id}
                  product={product}
                  selectedProducts={selectedProducts}
                  setSelectedProducts={setSelectedProducts}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      <AnimatePresence>
        {showPostModal && (
          <ProductsPostModal
            onClose={() => setShowPostModal(false)}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductsTable;
